import { INITIAL_FORM_DATA } from './triggerFormConstants.js';

// Helper function to update form data
export const createUpdateFormData = (setFormData) => (field, value) => {
    if (field?.includes('.')) {
        // Handle nested fields like 'action.actionType'
        const [parent, child] = field.split('.');
        setFormData((prev) => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [child]: value,
            },
        }));
    } else {
        // Handle top-level fields
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    }
};

// Helper to update multiple fields at once
export const createUpdateMultipleFields = (setFormData) => (updates) => {
    setFormData((prev) => ({
        ...prev,
        ...updates,
    }));
};

// Helper to reset form
export const createResetForm =
    (setFormData, initialData = {}) =>
    () => {
        setFormData({
            ...INITIAL_FORM_DATA,
            ...initialData,
        });
    };

// Helper to get initial form data
export const getInitialFormData = (initialData = {}) => ({
    ...INITIAL_FORM_DATA,
    ...initialData,
});

// A webhook body is a template, not a finished document: the Fields palette
// inserts [Placeholder] tokens that the backend substitutes when the trigger
// fires, so the raw text does not parse as JSON on its own. Standing each token
// in as null leaves real syntax errors detectable without rejecting a
// legitimate templated payload. Genuine arrays survive the swap too — a valid
// [1, 2] simply becomes null, which still parses.
const PLACEHOLDER_TOKEN = /\[[^[\]]*\]/g;

/**
 * Whether a webhook JSON body holds a syntax error the user must fix.
 *
 * An empty body is allowed; the field is optional. A body that is nothing but
 * placeholders is a pure template — existing triggers store bodies like
 * "[TriggerName][TriggerID]" — so there is no JSON structure to check.
 * Anything else has to parse once its placeholders stand in as null.
 */
export const isJsonBodyInvalid = (body) => {
    if (typeof body !== 'string' || !body.trim()) return false;
    if (!body.replace(PLACEHOLDER_TOKEN, '').trim()) return false;

    try {
        JSON.parse(body.replace(PLACEHOLDER_TOKEN, 'null'));
        return false;
    } catch (error) {
        return true;
    }
};

/**
 * Whether the form's webhook body is currently unparseable.
 *
 * Read straight off the form data rather than Formik's errors: the form
 * reinitialises on every keystroke (its initialValues are the context form
 * data), which clears field-level errors, so the JSON error only survived once
 * blur revalidated the field — leaving Save enabled until then (2930).
 */
export const hasInvalidWebhookBody = (formData) =>
    formData?.action?.actionType === 'WebHook' &&
    isJsonBodyInvalid(formData?.action?.body);

/**
 * Whether the JSON body should block Save.
 *
 * Saved triggers can already hold a body that is neither JSON nor a template,
 * so validating one the user has not touched would lock them out of editing
 * anything else on that trigger (2930).
 */
export const shouldBlockOnJsonBody = (formData, initialFormData) =>
    formData?.action?.body !== initialFormData?.action?.body &&
    hasInvalidWebhookBody(formData);
